import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SecureStorageService } from '../storage/secure-storage.service';
import { OrganizationCodeModel, OrganizationTokenModel } from 'src/app/api/proxy/organization/organization-models';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { AuthService } from '../auth-service/auth.service';
import { RequestOtpModel, RequestOtpResponseModel, VerifyOtpRequestModel } from 'src/app/api/proxy/auth/authentication-models';
import { AccessTokenModel, ProfileModel, RegisterModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import {
  isBomaidRegistrationDataComplete as isBomaidRegistrationDataCompleteUtil,
  isStandardRegistrationDataComplete as isStandardRegistrationDataCompleteUtil,
  isStandardRegistrationProfileComplete as isStandardRegistrationProfileCompleteUtil,
  buildBomaidRegistrationModel,
  resolveBomaidRegistrationOrganizationId,
  type BomaidRegistrationUserFields,
} from './registration-profile.util';
import { firstValueFrom, timeout } from 'rxjs';
import { VitalScoreApiConfiguration } from 'src/app/api/proxy/vitalscore/vital-score-api-configuration';
import { bomaidRegistrationPost, profileRegisterProfilePost } from 'src/app/api/proxy/vitalscore/functions';
import { MPIApiConfiguration } from 'src/app/api/proxy/mpi/mpi-api-configuration';
import { consumerValidateIdentifierPerOrganizationOrganizationIdGet } from 'src/app/api/proxy/mpi/functions';
import { PersonIdentifierType } from 'src/app/api/proxy/mpi/models/person-identifier-type';
import { SystemLogService } from '../systemlog-service/systemlog.service';
import { EnvironmentConfigService } from '../environment-config-service/environment-config.service';
import { OrganizationService } from '../organization-service/organization.service';
import { PromptService } from '../promtp-service/prompt.service';
import { TranslatorService } from '../translator-service/translator.service';
import { LocaleService } from '../locale-service/locale.service';
import { matchSupportedLocale } from '../locale-service/locale.util';
import { LoadingService } from '../loading-service/loading.service';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { normalizeMobileNumberE164 } from 'src/app/utilities/phone-number/phone-number.util';
import { EventService } from '../events/event.service';

/** Result of registration HTTP flows for page-level error handling (avoid duplicate popups). */
export interface RegistrationSubmitResult {
  success: boolean;
  /** True if the service already showed an error dialog — omit page-level generic message. */
  errorDialogAlreadyShown: boolean;
  /** Optional user-facing detail when no dialog was shown (or for logging / richer page messages). */
  errorDetail?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {

  private REGISTER_TIMEOUT = 1000 * 60 * 2;//2 minutes
  private NETWORK_TIMEOUT = 25000;

  /** See `registration-profile.util.ts` (profile-detail / OTP / create-password validation). */
  public static isStandardRegistrationProfileComplete(model: RegisterModel | null | undefined): boolean {
    return isStandardRegistrationProfileCompleteUtil(model);
  }

  /** See `registration-profile.util.ts` (includes password for RegisterProfile). */
  public static isStandardRegistrationDataComplete(model: RegisterModel | null | undefined): boolean {
    return isStandardRegistrationDataCompleteUtil(model);
  }

  /** See `registration-profile.util.ts` (standard fields + Bomaid policy number). */
  public static isBomaidRegistrationDataComplete(model: RegisterModel | null | undefined): boolean {
    return isBomaidRegistrationDataCompleteUtil(model);
  }

  private registrationStorageModel?: RegistrationStorageModel | null;
  constructor(
    private _authService: AuthService,
    private http: HttpClient,
    private vitalScoreCfg: VitalScoreApiConfiguration,
    private mpiCfg: MPIApiConfiguration,
    private _environmentService: EnvironmentConfigService,
    private _orgnaizationService: OrganizationService,
    private _ProfileService: MyProfileService,
    private _PromptService: PromptService,
    private _translator: TranslatorService,
    private _localeService: LocaleService,
  ) {

  }

  /** Allowlisted BCP-47 culture for registration payloads. */
  private resolveRegistrationLanguageCode(candidate?: string | null): string {
    return matchSupportedLocale(
      candidate || this._localeService.getCulture(),
      this._localeService.getAvailableLocales(),
      this._localeService.getDefaultCulture(),
    ).culture;
  }

  /**
   * Returns true when the identifier already exists for a person in the given organisation.
   * Anonymous MPI lookup — no bearer token required.
   */
  public async validateIdentifierPerOrganization(
    organizationId: string,
    personIdentifierType: PersonIdentifierType,
    identifierValue: string
  ): Promise<boolean> {
    const trimmed = identifierValue?.trim() ?? '';
    if (!organizationId || !trimmed) {
      return false;
    }

    try {
      const obs = consumerValidateIdentifierPerOrganizationOrganizationIdGet(this.http, this.mpiCfg.rootUrl, {
        organizationId,
        personIdentifierType,
        identifierValue: trimmed,
      });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      return httpResponse.body ?? false;
    } catch (error) {
      SystemLogService.logError(error, 'RegistrationService', 'validateIdentifierPerOrganization');
      throw error;
    }
  }

  private async resolveRegistrationOrganizationId(model: RegisterModel): Promise<string | null> {
    const defaultOrg = await this._orgnaizationService.GetDefaultOrganization();
    if (defaultOrg?.id) {
      return defaultOrg.id;
    }

    const selectedOrg = await this.getSelectedOrganization();
    if (selectedOrg?.id) {
      return selectedOrg.id;
    }

    const linkCode = model.organizationLinkCode?.trim();
    if (linkCode?.length === 6) {
      const org = await this._orgnaizationService.GetByTokenAsync(linkCode);
      return org?.id ?? null;
    }

    return null;
  }

  public async getSelectedOrganization(): Promise<OrganizationCodeModel | null | undefined> {
    const mdl = await SecureStorageService.get(SecureStorageKey.RegistrationModel);
    if (mdl) {
      this.registrationStorageModel = JSON.parse(mdl);
    } else {
      this.registrationStorageModel = new RegistrationStorageModel();
    }
    return this.registrationStorageModel?.selectedOrganization;
  }

  public async setSelectedOrganization(org: OrganizationCodeModel): Promise<boolean> {
    if (!this.registrationStorageModel) {
      this.registrationStorageModel = new RegistrationStorageModel();
    }
    this.registrationStorageModel.selectedOrganization = org;
    const model = await this.getRegistrationModel();
    if (model) {
      model.organizationLinkCode = org.shortCode!;
      this.registrationStorageModel.RegisterModel = model;
    }
    return await SecureStorageService.set(SecureStorageKey.RegistrationModel, JSON.stringify(this.registrationStorageModel));
  }

  /**
   * Pre-login fixed-org registration: set region, resolve org + consent, persist selection.
   * Used when environment.organizationId is configured (e.g. Patient Navigator).
   */
  public async prepareFixedOrganizationRegistration(): Promise<OrganizationTokenModel | null> {
    if (!environment.organizationId || !environment.countryCode) {
      return null;
    }

    let success = await this._environmentService.SetCountry(environment.countryCode, true);
    if (!success) {
      await this._environmentService.EnsureNetworkConfiguration('prepareFixedOrganizationRegistration');
      success = await this._environmentService.SetCountry(environment.countryCode, true);
      if (!success) {
        return null;
      }
    }

    let org = await this._orgnaizationService.ensureOrganizationForConsent();
    if (!org) {
      await this._environmentService.EnsureNetworkConfiguration('prepareFixedOrganizationRegistration');
      org = await this._orgnaizationService.ensureOrganizationForConsent();
    }
    if (!org) {
      return null;
    }

    await this.setSelectedOrganization(org);
    const brandedOrg = (await this._orgnaizationService.ensureOrganizationBranding()) ?? org;
    EventService.organizationConfigLoaded.emit(brandedOrg as OrganizationTokenModel);
    return brandedOrg as OrganizationTokenModel;
  }


  public async getRegistrationModel(): Promise<RegisterModel> {
    const mdl = await SecureStorageService.get(SecureStorageKey.RegistrationModel);
    if (mdl) {
      try {
        this.registrationStorageModel = JSON.parse(mdl);
      } catch (error) {
        console.warn('Failed to parse RegistrationModel from storage', error);
        // Keep any in-memory model already filled by the current submit flow.
      }
    } else if (!this.registrationStorageModel) {
      // Do not wipe an in-memory model when Preferences returns null (common race on native).
      this.registrationStorageModel = new RegistrationStorageModel();
    }

    if (!this.registrationStorageModel!.RegisterModel) {
      this.registrationStorageModel!.RegisterModel = {} as RegisterModel;
      // Do not persist empty {} — that overwrites a concurrent successful set.
    }

    return this.registrationStorageModel!.RegisterModel!;
  }

  public async setRegistrationModel(org: RegisterModel): Promise<boolean> {
    if (!this.registrationStorageModel) {
      this.registrationStorageModel = new RegistrationStorageModel();
    }
    this.registrationStorageModel.RegisterModel = org;
    return await SecureStorageService.set(SecureStorageKey.RegistrationModel, JSON.stringify(this.registrationStorageModel));
  }

  public async setPersonalDetails(name: string | null, surname: string | null, email: string | null): Promise<void> {
    let currentReg = await this.getRegistrationModel();
    if (!currentReg) {
      currentReg = {} as RegisterModel;
    }

    if (name) {
      currentReg.givenName = this.toTitleCase(name)!;
    }

    if (surname) {
      currentReg.surname = this.toTitleCase(surname)!;
    }

    if (email) {
      currentReg.emailAddress = email.trim().toLowerCase();
    }

    await this.setRegistrationModel(currentReg);
  }

  toTitleCase(str: string | undefined | null): string | undefined | null {
    if (str) {
      str = str.trim();
    }

    if (!str) {
      return str;
    }

    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }


  public async clear(): Promise<boolean> {
    this.externalProfileModel = null;
    return await SecureStorageService.remove(SecureStorageKey.RegistrationModel);
  }

  private _LastOTPRequest: RequestOtpResponseModel | null = null;

  public get lastOTPRequest(): RequestOtpResponseModel | null {
    return this._LastOTPRequest;
  }

  public async requestOTP(): Promise<RequestOtpResponseModel | null> {
    const model = await this.getRegistrationModel();
    if (!model || !model.emailAddress)
      return null;

    const otpRequestModel: RequestOtpModel = {
      emailAddress: model.emailAddress
    }
    this._LastOTPRequest = await this._authService.requestOTP(otpRequestModel);
    return this._LastOTPRequest
  }

  public async verifyOTP(otp: string): Promise<boolean> {
    const model = await this.getRegistrationModel();
    if (!model || !model.emailAddress)
      return false;

    const mdl: VerifyOtpRequestModel = {
      emailAddress: model.emailAddress,
      otp: parseInt(otp)
    };

    const result = await this._authService.verifyOTP(mdl);
    if (result) {
      model.emailAddressOTP = parseInt(otp);
      await this.setRegistrationModel(model);
    }
    return result;

  }

  /**
   * @param preparedModel When provided (Bomaid registration page), use this payload instead of
   * reloading from Preferences — avoids native storage races that posted blank email/password/policy.
   */
  public async bomaidRegister(preparedModel?: RegisterModel | null): Promise<RegistrationSubmitResult> {
    if (Capacitor.isNativePlatform()) {
      if (!environment.enableMobileRegistation) {
        return { success: false, errorDialogAlreadyShown: false };
      }
    } else {
      if (!environment.enableWebRegistation) {
        return { success: false, errorDialogAlreadyShown: false };
      }
    }

    const userRegistrationModel = preparedModel
      ? ({ ...preparedModel } as RegisterModel)
      : await this.getRegistrationModel();
    try {
      if (!userRegistrationModel) {
        throw new Error("No registration model found");
      }

      userRegistrationModel.emailAddress = (userRegistrationModel.emailAddress ?? '').trim().toLowerCase();
      userRegistrationModel.mobileNumber = normalizeMobileNumberE164(userRegistrationModel.mobileNumber);
      userRegistrationModel.givenName = this.toTitleCase(userRegistrationModel.givenName) ?? '';
      userRegistrationModel.surname = this.toTitleCase(userRegistrationModel.surname) ?? '';
      userRegistrationModel.password = (userRegistrationModel.password ?? '').trim();
      (userRegistrationModel as { policyNumber?: string }).policyNumber =
        String((userRegistrationModel as { policyNumber?: string }).policyNumber ?? '').trim();

      await this.setRegistrationModel(userRegistrationModel);

      if (!RegistrationService.isBomaidRegistrationDataComplete(userRegistrationModel)) {
        const missing = this.describeIncompleteBomaidRegistration(userRegistrationModel);
        SystemLogService.logError(
          new Error('BomaidRegister: incomplete registration data'),
          'RegistrationService',
          `BomaidRegister skipped — missing: ${missing}`
        );
        return {
          success: false,
          errorDialogAlreadyShown: false,
          errorDetail: this._translator.translateText('registration-service.error.incomplete-fields', { fields: missing }),
        };
      }

      const envConfig = await this._environmentService.EnsureNetworkConfiguration("Bomaid Registration");
      const countryCode = await this._environmentService.CurrentCountry();

      // Bomaid registration always posts environment.organizationId as registrationOrganizationId.
      const registrationOrganizationId = resolveBomaidRegistrationOrganizationId(environment.organizationId);

      let org = await this._orgnaizationService.GetDefaultOrganization();
      if ((!org?.id || org.id !== registrationOrganizationId) && registrationOrganizationId) {
        org = await this.prepareFixedOrganizationRegistration();
        if (!org?.id) {
          org = await this._orgnaizationService.GetDefaultOrganization();
        }
      }

      if (!envConfig || !envConfig.authenticateOptions || !envConfig.authenticateOptions.appId || !envConfig.authenticateOptions.scope) {
        throw new Error("Application configuration not available");
      }

      if (!countryCode || !countryCode.countryCode) {
        throw new Error("Country code not available");
      }

      const bomaidRegistrationModel = buildBomaidRegistrationModel({
        applicationId: envConfig.authenticateOptions.appId,
        applicationScope: envConfig.authenticateOptions.scope,
        languageCode: this.resolveRegistrationLanguageCode(userRegistrationModel.languageCode),
        environmentOrganizationId: environment.organizationId,
        user: userRegistrationModel as BomaidRegistrationUserFields,
      });

      if (!bomaidRegistrationModel?.registrationOrganizationId) {
        SystemLogService.logError(
          new Error('BomaidRegister: missing registrationOrganizationId'),
          'RegistrationService',
          'BomaidRegister skipped — environment.organizationId not configured'
        );
        return {
          success: false,
          errorDialogAlreadyShown: false,
          errorDetail: this._translator.translateText('registration-service.error.organization-required'),
        };
      }

      const registrationOrganizationIdForConsent = bomaidRegistrationModel.registrationOrganizationId;

      let promiseResult: AccessTokenModel | null = null;
      let httpErrorDialogShown = false;

      try {
        const obs = bomaidRegistrationPost(this.http, this.vitalScoreCfg.rootUrl, { body: bomaidRegistrationModel });
        const httpResponse = await firstValueFrom(obs.pipe(timeout(this.REGISTER_TIMEOUT)));
        promiseResult = httpResponse.body!;
      } catch (error) {
        console.error(error);
        await LoadingService.closeAllLoaders();
        const serverMessage = this._PromptService.tryGetServerErrorMessage(error);
        if (serverMessage) {
          await this._PromptService.showError(serverMessage);
          httpErrorDialogShown = true;
        } else {
          await this._PromptService.showNetworkConnectionError(error, 'BomaidRegister', true);
        }
        SystemLogService.logError(error, 'RegistrationService', 'BomaidRegister' + JSON.stringify(bomaidRegistrationModel));
      }

      if (promiseResult && promiseResult.access_token) {
        await SecureStorageService.clear();
        await this._environmentService.SaveEnvironmentConfig(envConfig!);
        await this._environmentService.SetCountry(countryCode!.countryCode!, true);
        if (org) {
          await this._orgnaizationService.SetDefaultOrganization(org);
        }

        await this._authService.registerProfile(promiseResult);
        await this._orgnaizationService.GrantConsentForOrganization(registrationOrganizationIdForConsent);

        return { success: true, errorDialogAlreadyShown: false };
      }

      return { success: false, errorDialogAlreadyShown: httpErrorDialogShown };
    } catch (error: unknown) {
      console.error(error);

      const loggedModel = preparedModel
        ? ({ ...preparedModel } as RegisterModel)
        : await this.getRegistrationModel();
      if (loggedModel) {
        loggedModel.password = 'replacedthepassword';
        SystemLogService.logError(error, 'RegistrationService', 'BomaidRegister' + JSON.stringify(loggedModel));
      }

      return { success: false, errorDialogAlreadyShown: false };
    }
  }

  /**
   * @param grantOrganizationConsent Default true; pass false for client/hca startup page inline registration (no consent UI).
   * @param preparedModel When provided (inline register pages), use this payload instead of reloading from Preferences.
   */
  public async RegisterProfile(
    skipOtpVerification: boolean = false,
    grantOrganizationConsent: boolean = true,
    preparedModel?: RegisterModel | null
  ): Promise<RegistrationSubmitResult> {

    if (Capacitor.isNativePlatform()) {
      if (!environment.enableMobileRegistation) {
        return {
          success: false,
          errorDialogAlreadyShown: false,
          errorDetail: this._translator.translateText('registration-service.error.mobile-registration-disabled'),
        };
      }
    } else {
      if (!environment.enableWebRegistation) {
        return {
          success: false,
          errorDialogAlreadyShown: false,
          errorDetail: this._translator.translateText('registration-service.error.web-registration-disabled'),
        };
      }
    }

    if (this.externalProfileModel != null) {
      const extOk = await this.externalRegistration();
      return extOk
        ? { success: true, errorDialogAlreadyShown: false }
        : {
            success: false,
            errorDialogAlreadyShown: false,
            errorDetail: this._translator.translateText('registration-service.error.external-registration-failed'),
          };
    }

    const userRegistrationModel = preparedModel
      ? { ...preparedModel }
      : await this.getRegistrationModel();
    try {
      if (!userRegistrationModel) {
        throw new Error(this._translator.translateText('registration-service.error.no-registration-model'));
      }

      userRegistrationModel.emailAddress = (userRegistrationModel.emailAddress ?? '').trim().toLowerCase();
      userRegistrationModel.mobileNumber = normalizeMobileNumberE164(userRegistrationModel.mobileNumber);
      userRegistrationModel.givenName = this.toTitleCase(userRegistrationModel.givenName) ?? '';
      userRegistrationModel.surname = this.toTitleCase(userRegistrationModel.surname) ?? '';
      userRegistrationModel.password = (userRegistrationModel.password ?? '').trim();
      userRegistrationModel.organizationLinkCode = (userRegistrationModel.organizationLinkCode ?? '').trim();
      userRegistrationModel.ignoreEmailOTP = null;
      if (skipOtpVerification) {
        userRegistrationModel.ignoreEmailOTP = true;
      }
      userRegistrationModel.languageCode = this.resolveRegistrationLanguageCode(userRegistrationModel.languageCode);
      await this.setRegistrationModel(userRegistrationModel);

      if (!RegistrationService.isStandardRegistrationDataComplete(userRegistrationModel)) {
        const missing = this.describeIncompleteRegistration(userRegistrationModel);
        SystemLogService.logError(
          new Error('RegisterProfile: incomplete registration data'),
          'RegistrationService',
          `RegisterProfile skipped — missing: ${missing}`
        );
        return {
          success: false,
          errorDialogAlreadyShown: false,
          errorDetail: this._translator.translateText('registration-service.error.incomplete-fields', { fields: missing }),
        };
      }

      const additionalIdentifier = userRegistrationModel.additionalIdentifier?.trim();
      if (additionalIdentifier && userRegistrationModel.additionalIdentifierType != null) {
        try {
          const organizationId = await this.resolveRegistrationOrganizationId(userRegistrationModel);
          if (!organizationId) {
            await this._PromptService.showError(
              this._translator.translateText('hwh-register-page.inline-registration.error.organization-required-for-identifier')
            );
            return { success: false, errorDialogAlreadyShown: true };
          }

          const identifierExists = await this.validateIdentifierPerOrganization(
            organizationId,
            userRegistrationModel.additionalIdentifierType as PersonIdentifierType,
            additionalIdentifier
          );
          if (identifierExists) {
            await this._PromptService.showError(
              this._translator.translateText('hwh-register-page.inline-registration.error.identifier-already-exists')
            );
            return { success: false, errorDialogAlreadyShown: true };
          }
        } catch (error) {
          const detail = this._PromptService.formatRegistrationFailureDetail(
            error,
            this._translator.translateText('registration-service.error.identifier-validation-failed')
          );
          await this._PromptService.showError(detail);
          return { success: false, errorDialogAlreadyShown: true, errorDetail: detail };
        }
      }

      let promiseResult: AccessTokenModel | null = null;
      let httpErrorDialogShown = false;
      let httpErrorDetail: string | undefined;

      try {
        const obs = profileRegisterProfilePost(this.http, this.vitalScoreCfg.rootUrl, { body: userRegistrationModel });
        const httpResponse = await firstValueFrom(obs.pipe(timeout(this.REGISTER_TIMEOUT)));
        promiseResult = httpResponse.body!;
      } catch (error) {
        console.error(error);
        await LoadingService.closeAllLoaders();
        httpErrorDetail = this._PromptService.formatRegistrationFailureDetail(
          error,
          this._translator.translateText('registration-service.error.request-failed')
        );
        await this._PromptService.showError(httpErrorDetail);
        httpErrorDialogShown = true;
        SystemLogService.logError(error, 'RegistrationService', ' RegisterProfile' + JSON.stringify(userRegistrationModel));
      }

      if (promiseResult && promiseResult.access_token) {
        try {
          const countryCode = await this._environmentService.CurrentCountry();
          const envConfig = await this._environmentService.EnsureNetworkConfiguration("Registration service");
          const org = await this._orgnaizationService.GetDefaultOrganization();

          await SecureStorageService.clear();
          if (envConfig) {
            await this._environmentService.SaveEnvironmentConfig(envConfig);
          }
          if (countryCode?.countryCode) {
            await this._environmentService.SetCountry(countryCode.countryCode, true);
          }
          if (org) {
            await this._orgnaizationService.SetDefaultOrganization(org);
          }

          await this._authService.registerProfile(promiseResult);
          if (org?.id) {
            try {
              await this._orgnaizationService.GrantConsentForOrganization(org.id, grantOrganizationConsent);
            } catch (consentError) {
              // Account + token already created — do not fail registration for consent follow-up.
              SystemLogService.logError(
                consentError,
                'RegistrationService',
                'RegisterProfile-GrantConsent'
              );
            }
          }
        } catch (postTokenError) {
          // Prefer keeping the issued token over reporting a false create failure.
          SystemLogService.logError(
            postTokenError,
            'RegistrationService',
            'RegisterProfile-postToken'
          );
          try {
            await this._authService.registerProfile(promiseResult);
          } catch (tokenSaveError) {
            SystemLogService.logError(
              tokenSaveError,
              'RegistrationService',
              'RegisterProfile-tokenSaveFallback'
            );
            return {
              success: false,
              errorDialogAlreadyShown: false,
              errorDetail: this._translator.translateText('registration-service.error.token-save-failed'),
            };
          }
        }

        return { success: true, errorDialogAlreadyShown: false };
      }

      if (httpErrorDialogShown) {
        return { success: false, errorDialogAlreadyShown: true, errorDetail: httpErrorDetail };
      }

      const tokenError =
        (promiseResult as { error_description?: string; error?: string; message?: string } | null)?.error_description ||
        (promiseResult as { error?: string } | null)?.error ||
        (promiseResult as { message?: string } | null)?.message ||
        this._translator.translateText('registration-service.error.no-access-token');

      return {
        success: false,
        errorDialogAlreadyShown: false,
        errorDetail: String(tokenError),
      };
    } catch (error: unknown) {
      console.error(error);

      if (userRegistrationModel) {
        userRegistrationModel.password = 'replacedthepassword';
        SystemLogService.logError(error, 'RegistrationService', 'RegisterProfile' + JSON.stringify(userRegistrationModel));
      }

      const detail = this._PromptService.formatRegistrationFailureDetail(
        error,
        error instanceof Error && error.message
          ? error.message
          : this._translator.translateText('registration-service.error.unexpected')
      );
      return { success: false, errorDialogAlreadyShown: false, errorDetail: detail };
    }
  }

  private describeIncompleteRegistration(model: RegisterModel): string {
    const missing: string[] = [];
    if (!(model.emailAddress ?? '').trim().includes('@')) {
      missing.push('email');
    }
    if (!(model.givenName ?? '').trim()) {
      missing.push('givenName');
    }
    if (!(model.surname ?? '').trim()) {
      missing.push('surname');
    }
    if (!(model.dateOfBirth ?? '').toString().trim()) {
      missing.push('dateOfBirth');
    }
    if (!(model.applicationId ?? '').toString().trim()) {
      missing.push('applicationId');
    }
    if (model.gender === null || model.gender === undefined) {
      missing.push('gender');
    }
    if (!(model.password ?? '').trim()) {
      missing.push('password');
    }
    return missing.length ? missing.join(', ') : 'unknown';
  }

  private describeIncompleteBomaidRegistration(model: RegisterModel): string {
    const missing = this.describeIncompleteRegistration(model);
    const policyNumber = String((model as { policyNumber?: string }).policyNumber ?? '').trim();
    if (!policyNumber) {
      return missing === 'unknown' ? 'policyNumber' : `${missing}, policyNumber`;
    }
    return missing;
  }

  externalProfileModel: ProfileModel | null = null;
  public async setExternalRegister(model: ProfileModel) {
    this.externalProfileModel = model;
  }

  public isExternalLogin(): boolean {
    return (this.externalProfileModel != null);
  }

  private async externalRegistration(): Promise<boolean> {
    if (this.externalProfileModel) {
      this.externalProfileModel.languageCode = this.resolveRegistrationLanguageCode(
        this.externalProfileModel.languageCode,
      );
      let result = await this._ProfileService.SaveProfile(this.externalProfileModel);
      if (result) {
        let org = await this._orgnaizationService.GetDefaultOrganization();
        if (org?.id) {
          await this._orgnaizationService.GrantConsentForOrganization(org.id);
          this.clear();
          return true;
        }
      }
    }
    return false;

  }
}


class RegistrationStorageModel {
  selectedOrganization?: OrganizationCodeModel | null;
  RegisterModel?: RegisterModel | null;
}
